import { useNavigate } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import { usePlacesAutocomplete } from "@/hooks/usePlacesAutocomplete";

import listItem from "@/types/list";
import { StrapiImage } from "@/types/strapi";
import { mapImage } from "@/utils/imageHelper";

import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import styles from "./HowItWorks.module.css";

interface HowItWorksStepProps {
	label: string;
	title: string;
	listItems: listItem[];
	image: StrapiImage;
}

export default function HowItWorks({ listItems, title, image, label }: HowItWorksStepProps) {
	const navigate = useNavigate();
	const mappedHeroImage = mapImage(image as StrapiImage, true);

	const worksInputRef = useRef<HTMLInputElement>(null);
	const [providerName, setProviderName] = useState("");

	const { place: worksPlace } = usePlacesAutocomplete({
		inputRef: worksInputRef,
		types: ["health"],
		componentRestrictions: { country: "us" },
		fields: ["place_id", "name"],
	});

	useEffect(() => {
		if (!worksPlace?.name) return;
		setProviderName(worksPlace.name);
	}, [worksPlace]);

	return (
		<section id="how-it-works" className="py-20 xl:pb-40 xl:pt-[133px] bg-white">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-0">
				<div className={styles.sectionHeader}>
					<h5 className="text-lg leading-8 uppercase font-medium text-black font-karla mb-4">
						{label}
					</h5>
					<h2 className="font-normal text-black font-abc-prophet text-4xl leading-tight lg:text-[58px] lg:leading-[76px]">
						{title}
					</h2>
				</div>
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
					{/* Left Column - Image */}
					<motion.div
						initial={{ opacity: 0, x: 50 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8 }}
						viewport={{ once: true }}
						className="space-y-8"
					>
						<ul className={styles.listItems}>
							{listItems.map((step, index) => (
								<motion.li
									key={step.id}
									initial={{ opacity: 0, x: 30 }}
									whileInView={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.8, delay: index * 0.2 }}
									viewport={{ once: true }}
									className="flex items-start space-x-4"
								>
									<div className="pl-6">
										{
											step.title && (
												<h3 className="text-lg lg:text-[32px] lg:leading-[46px] font-normal text-black font-abc-prophet mb-2">
													{step.title}
												</h3>
											)
										}

										<p className="text-black font-karla leading-relaxed lg:text-xl lg:leading-8">
											{step.content}
										</p>
									</div>
								</motion.li>
							))}
						</ul>
					</motion.div>

					{/* Right Column - Content */}
					<motion.div
						initial={{ opacity: 0, x: -50 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8 }}
						viewport={{ once: true }}
						className="flex justify-center lg:justify-start"
					>
						<img
							src={mappedHeroImage?.url}
							alt={mappedHeroImage?.alternativeText}
							className="w-auto h-auto max-h-full max-w-full"
						/>
					</motion.div>
				</div>

				<div className={styles.searchMidWife}>
					{/* Search Input */}
					<div className="flex flex-col sm:flex-row gap-4 lg:gap-7">
						<Input
							ref={worksInputRef}
							id="how-it-works-location-input"
							placeholder="Search for your OBGYN/Midwife partner"
							className="text-xl font-karla text-black placeholder:opacity-50 h-auto shadow-btn-shadow py-[14px] pl-[30px] pr-3 border-newTheme-lightGrey"
							type="search"
							value={providerName}
							onChange={(e) => setProviderName(e.target.value)}
						/>
						<Button
							type="button"
							onClick={() => navigate("/search-doulas")}
							className="bg-newTheme-lime hover:bg-newTheme-orange text-black h-auto py-[14px] px-[20px] shadow-btn-shadow font-karla text-xl"
						>
							Find Your Doula
						</Button>
					</div>
				</div>
			</div>
		</section>
	)
}